'use client';

import {useBEApiClient} from '@hooks/network/useApiClient';
import type {PropsWithChildren} from 'react';
import {useEffect, useRef} from 'react';
import useSWRImmutable from 'swr/immutable';

import {McpManifestsContext, createMcpManifestsStore} from './store';

type Props = PropsWithChildren<{
  // Internal manifests are opt-in: nest this provider around the subtree that needs them
  // (it overrides the app-level one for its children).
  includeInternal?: boolean;
}>;

export const McpManifestsProvider = (props: Props) => {
  const {children, includeInternal = false} = props;
  const api = useBEApiClient();

  const {data: {data: manifests} = {}, isLoading} = useSWRImmutable(
    ['mcp-manifests', includeInternal],
    () => api?.publicListMcpManifests({limit: 400, include_internal: includeInternal}),
    {
      errorRetryCount: 10,
      keepPreviousData: true,
      revalidateOnReconnect: true,
    },
  );

  const storeRef = useRef<ReturnType<typeof createMcpManifestsStore> | null>(createMcpManifestsStore());

  useEffect(() => {
    if (manifests && !isLoading) {
      storeRef.current?.getState().setManifests(manifests.items);
    }
  }, [manifests, isLoading]);

  useEffect(() => {
    storeRef.current?.getState().setIsManifestsLoading(isLoading);
  }, [isLoading]);

  return <McpManifestsContext.Provider value={storeRef.current}>{children}</McpManifestsContext.Provider>;
};
